import React, { useEffect, useMemo, useState } from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
import LeftArrowIcon from '@atlaskit/icon/glyph/arrow-left';
import RightArrowIcon from '@atlaskit/icon/glyph/arrow-right';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { token, useThemeObserver } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import rightSectionBgDark from '../../common/assets/right_section_bg_dark.svg';
import rightSectionBgLight from '../../common/assets/right_section_bg_light.svg';
import stepFiveVector from '../../common/assets/step_five_vector.png';
import stepFourVectorDark from '../../common/assets/step_four_vector_dark.png';
import stepFourVectorLight from '../../common/assets/step_four_vector_light.png';
import stepOneVectorDark from '../../common/assets/step_one_vector_dark.png';
import stepOneVectorLight from '../../common/assets/step_one_vector_light.png';
import stepThreeVectorDark from '../../common/assets/step_three_vector_dark.png';
import stepThreeVectorLight from '../../common/assets/step_three_vector_light.png';
import stepTwoVectorDark from '../../common/assets/step_two_vector_dark.png';
import stepTwoVectorLight from '../../common/assets/step_two_vector_light.png';
import { useGlobalChangeboarding } from '../../controllers/store/global';
import { useOgConsolidationUserState } from '../../controllers/use-og-consolidation-user-state';
import { UNLOCKED_STEP_ID } from './constants';
import { messages } from './messages';
import { StepFour, StepOne, StepThree, StepTwo, StepFive } from './steps';

interface WizardProps {
	isOpen: boolean;
	onClose: () => void;
	defaultStep?: number;
	onUnlock?: () => Promise<void>;
}

export const Wizard = ({ isOpen, onClose, defaultStep = 0, onUnlock }: WizardProps) => {
	const { formatMessage } = useIntl();
	const theme = useThemeObserver();
	const isDarkMode = theme.colorMode === 'dark';

	const [currentStep, setCurrentStep] = useState(defaultStep);
	const { update } = useOgConsolidationUserState();

	const [isUnlocking, setIsUnlocking] = useState(false);
	const { activateTransitionHome, openTransitionHomeModal, isTransitionHomeLoading } =
		useGlobalChangeboarding();

	const steps = [
		{
			id: 'step-1',
			component: <StepOne />,
			vector: { light: stepOneVectorLight, dark: stepOneVectorDark },
		},
		{
			id: 'step-2',
			component: <StepTwo />,
			vector: { light: stepTwoVectorLight, dark: stepTwoVectorDark },
		},
		{
			id: 'step-3',
			component: <StepThree />,
			vector: { light: stepThreeVectorLight, dark: stepThreeVectorDark },
		},
		{
			id: 'step-4',
			component: <StepFour />,
			vector: { light: stepFourVectorLight, dark: stepFourVectorDark },
		},
		{
			id: 'step-5',
			// If started from step 5, it means we weren't the one unlocking it
			component: <StepFive isFirstUnlocked={defaultStep !== UNLOCKED_STEP_ID} />,
			// todo: update the dark vector when it is ready
			vector: { light: stepFiveVector, dark: stepFiveVector },
		},
	];

	const isFirstStep = currentStep === 0;
	const isUnlockStep = currentStep === 3;
	const isUnlockedStep = currentStep === UNLOCKED_STEP_ID;

	useEffect(() => {
		// Immediately update user state when entering the last step
		if (isUnlockedStep && isOpen) {
			update({
				isWelcomeModalDisplayed: true,
			}).catch(() => {
				// If an error occurs, close the modal
				onClose();
			});
		}
		// Here we don't include onClose as it is probably an inlined function
		// with a state update. It is totally fine to call the initial function
		// passed in when the update is first called even though it is now stale.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isUnlockedStep, update, isOpen]);

	const handlePrevButton = (e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		if (isFirstStep) {
			onClose();
		} else if (isUnlockedStep) {
			fireUIAnalytics(analyticsEvent, 'closeTransitionWizardInUnlockStep');
			onClose();
			activateTransitionHome();
		} else {
			setCurrentStep((prevState) => prevState - 1);
		}
	};

	const handleNextButton = () => {
		if (isUnlockStep) {
			// should send request to BE for unlock
			setIsUnlocking(true);
			onUnlock?.()
				.then(() => {
					setCurrentStep((prevState) => prevState + 1);
				})
				.finally(() => {
					setIsUnlocking(false);
				});
			return;
		}
		if (isUnlockedStep) {
			activateTransitionHome();
			openTransitionHomeModal().then(() => {
				onClose();
			});
			return;
		}
		setCurrentStep((prevState) => prevState + 1);
	};

	const primaryButtonText = useMemo(() => {
		if (isUnlockStep) {
			return formatMessage(messages.unlockButton);
		}
		if (isUnlockedStep) {
			return formatMessage(messages.getStartedButton);
		}
		return formatMessage(messages.nextButton);
	}, [formatMessage, isUnlockStep, isUnlockedStep]);

	const secondaryButtonText = useMemo(() => {
		if (isFirstStep) {
			return formatMessage(messages.closeButton);
		}
		if (isUnlockedStep) {
			return formatMessage(messages.remindMeLaterButton);
		}
		return formatMessage(messages.backButton);
	}, [formatMessage, isFirstStep, isUnlockedStep]);

	return (
		<ModalTransition>
			{isOpen && (
				<ShortcutScope>
					<Modal height="700px" width={1100} onClose={onClose} shouldCloseOnOverlayClick={false}>
						<Flex xcss={contentContainerStyles}>
							<Flex
								direction="column"
								xcss={[
									halfWidthStyles,
									leftColumnStyles,
									isUnlockedStep && leftColumnUnlockedStyles,
								]}
								justifyContent={isUnlockedStep ? 'end' : 'center'}
								rowGap="space.400"
							>
								<Flex direction="column" rowGap="space.100">
									{!isUnlockedStep && (
										<Box as="span" xcss={locatorStyles}>
											{formatMessage(messages.stepLocator, {
												stepNumber: currentStep + 1,
												// last step is unlocked step
												totalSteps: steps.length - 1,
											})}
										</Box>
									)}
									<Box>{steps[currentStep].component}</Box>
								</Flex>
								<Flex xcss={actionsStyles} columnGap="space.100">
									<Button
										iconBefore={
											!isFirstStep && !isUnlockedStep ? <LeftArrowIcon label="" /> : undefined
										}
										onClick={handlePrevButton}
										appearance={isUnlockedStep ? 'subtle' : 'default'}
									>
										{secondaryButtonText}
									</Button>
									<LoadingButton
										iconAfter={
											!isUnlockStep && !isUnlockedStep ? <RightArrowIcon label="" /> : undefined
										}
										onClick={handleNextButton}
										appearance="primary"
										isLoading={isUnlocking || isTransitionHomeLoading}
									>
										{primaryButtonText}
									</LoadingButton>
								</Flex>
							</Flex>
							<Flex
								xcss={[
									halfWidthStyles,
									rightColumnStyles,
									isDarkMode ? rightColumnDarkStyles : rightColumnLightStyles,
								]}
							>
								<Box
									role="img"
									// eslint-disable-next-line jira/react/no-style-attribute
									style={{
										backgroundImage: `url(${
											isDarkMode
												? steps[currentStep].vector?.dark
												: steps[currentStep].vector?.light
										})`,
									}}
									xcss={rightVectorStyles}
								/>
							</Flex>
							<Box xcss={closeIconStyles}>
								<Button
									appearance="subtle"
									iconBefore={<CrossIcon label="" />}
									aria-label={formatMessage(messages.closeButton)}
									onClick={onClose}
								/>
							</Box>
						</Flex>
					</Modal>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};

const actionsStyles = xcss({ alignSelf: 'flex-end' });
const contentContainerStyles = xcss({ height: '100%', position: 'relative' });
const locatorStyles = xcss({ color: 'color.text.subtlest', font: token('font.body.small') });

const leftColumnStyles = xcss({ paddingLeft: 'space.800', paddingRight: 'space.800' });
const leftColumnUnlockedStyles = xcss({ paddingBottom: 'space.1000' });
const halfWidthStyles = xcss({ width: '50%', flexShrink: 0 });
const rightColumnStyles = xcss({
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	borderTopRightRadius: 'border.radius.100',
	borderBottomRightRadius: 'border.radius.100',
});
const rightVectorStyles = xcss({
	width: '100%',
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'contain',
	backgroundPosition: 'center',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const rightColumnLightStyles = xcss({ backgroundImage: `url(${rightSectionBgLight})` });
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const rightColumnDarkStyles = xcss({ backgroundImage: `url(${rightSectionBgDark})` });
const closeIconStyles = xcss({
	position: 'absolute',
	right: 'space.200',
	top: 'space.200',
});
